import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SectionClouds } from "./SectionClouds";

interface Benefit {
  titleLead: string;
  titleEm: string;
  titleTail?: string;
  desc: string;
}

export function Benefits() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const items = t("landing.benefits.items", { returnObjects: true }) as Benefit[];

  return (
    <section ref={ref} id="benefits" className="frame bg-surface-2/40">
      <SectionClouds variant="benefits" />
      <div className="container-narrow section-pad relative z-10">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">04 —</span> {t("landing.benefits.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.benefits.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.benefits.lede")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-7">
          {items.map((b, i) => (
            <div
              key={i}
              className={cn(
                "lift rounded-[22px] border border-line bg-surface p-8 sm:p-9",
                "transition-all duration-700",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
              )}
              style={{ transitionDelay: isVisible ? `${i * 80}ms` : "0ms" }}
            >
              <div className="mono text-[11px] tracking-[0.14em] text-accent">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-4 text-[clamp(24px,2.4vw,32px)] leading-[1.15] font-medium tracking-[-0.025em] text-balance text-ink">
                {b.titleLead} <span className="serif-italic text-accent">{b.titleEm}</span>
                {b.titleTail ? ` ${b.titleTail}` : ""}
              </h3>
              <p className="mt-4 text-[14.5px] leading-[1.6] text-ink-2">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
